import React from 'react';
import './Pagination.css';

/**
 * Shared Pagination component — exact design from Figma.
 *
 * Props:
 *  - currentPage  (number)  current active page (1-based)
 *  - totalPages   (number)  total number of pages
 *  - onPageChange (fn)      called with new page number
 *
 * Rules:
 *  - Hidden when totalPages <= 1
 *  - Shows max 5 page numbers with a sliding window
 */
const Pagination = ({ currentPage = 1, totalPages = 1, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;

  // Build page number array (max 5 visible, sliding window)
  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
  if (endPage - startPage + 1 < maxVisible) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  const pages = [];
  for (let i = startPage; i <= endPage; i++) pages.push(i);

  const goPrev = () => { if (currentPage > 1) onPageChange(currentPage - 1); };
  const goNext = () => { if (currentPage < totalPages) onPageChange(currentPage + 1); };

  return (
    <div className="gc-pagination">
      {/* Prev arrow */}
      <button
        className={`gc-pg-arrow gc-pg-prev ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={goPrev}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
          <path d="M7 13L1 7L7 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Page numbers */}
      {pages.map(num => (
        <span
          key={num}
          className={`gc-pg-num ${currentPage === num ? 'active' : ''}`}
          onClick={() => onPageChange(num)}
        >
          {num}
        </span>
      ))}

      {/* Next arrow */}
      <button
        className={`gc-pg-arrow gc-pg-next ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={goNext}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
          <path d="M1 13L7 7L1 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

export default Pagination;
